import {Component, computed, EventEmitter, inject, signal, ViewChild, type OnInit, effect, ElementRef} from '@angular/core';
import { OnDestroy } from '@angular/core';
import { DateTime } from 'luxon';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TicketDTO } from 'src/app/DTO/TicketDTO';
import { CommunityService } from 'src/app/services/community/community.service';
import { SwiperContainer } from 'swiper/element';
import { TicketsService } from './services/tickets.service';
import Swal from 'sweetalert2';
import { TranslateService } from '@ngx-translate/core';
import {CustomFieldDTO} from "../../../../DTO/CustomFieldDTO";
import { PurchaseResultDTO } from 'src/app/DTO/tickets/PurchaseResultDTO';
import { PaymentResult, PaymentsService } from 'src/app/services/payments/payments.service';
import { API_URL } from 'src/environments/environment';
import { CountdownEvent } from 'ngx-countdown';
import { firstValueFrom, Subscription } from 'rxjs';
import { HolderPurchaseData } from 'src/app/DTO/tickets/helpers/HolderPurchaseData';
import { CustomFieldsConstants } from 'src/app/constants/CustomFieldsConstants';
import { toSignal } from '@angular/core/rxjs-interop';
import { TicketPurchaseRequest } from 'src/app/DTO/tickets/requests/TicketPurchaseRequest';
import { ApiService } from 'src/app/services/api.service';
import { TicketWaitingListInfoDTO } from 'src/app/DTO/tickets/TicketWaitingListInfoDTO';
import { TicketCouponPreviewResponseDTO } from 'src/app/DTO/tickets/TicketCouponPreviewResponseDTO';
import { ticketI18nKey, TICKETS_AS_ENTRADAS_COMMUNITY } from 'src/app/helpers/ticket-translate.helper';
import { pushPurchaseEventToGtm } from 'src/app/helpers/gtm.helper';

@Component({
  selector: 'app-payment-modal',
  templateUrl: './payment-modal.component.html',
  styleUrls: ['./payment-modal.component.scss'],
})
export class PaymentModalComponent implements OnInit, OnDestroy {

  @ViewChild('swiper') private swiperRef: ElementRef;

  // Services
  private dialogConfig = inject(DynamicDialogConfig);
  private communityService = inject(CommunityService);
  private ticketsService = inject(TicketsService);
  public translate = inject(TranslateService);  
  private ref = inject(DynamicDialogRef);  
  private paymentsService = inject(PaymentsService);
  private apiService = inject(ApiService);

  public ticket = signal<TicketDTO | null>(null);
  public waitingListUserDetails: TicketWaitingListInfoDTO | null = null;
  public isMultiple = computed(() => this.ticket().multipleInscriptions)
    
  public formIsValid = signal(false);    
  
  public swiperLoaded = signal(false);  
  public customFieldsForm = signal<CustomFieldDTO[]>([]);
  
  public buttonLoading = signal(false);
  public buttonPrevLoading = signal<boolean>(false);    
  public fieldTypes = CustomFieldsConstants.InputTypes;
  
  // Reservation
  public reservedSeconds = signal<number | null>(null);  
  private reservedToken = signal<string | null>(null);  
  private reservedUntil = signal<DateTime | null>(null);  
  
  // Swiper
  private swiper: SwiperContainer;
  public currentSlide = signal(0);    
  public lastSlide = signal<number>(1);
  public canContinue = signal(false);

  // Tickets step
  public quantity = signal<number>(0)
  public totalPrice = signal<number | null>(null);
  public fullFree = computed(() => {
    return this.ticket().prices.every(x => x.totalWithFee == 0);
  });  
  public availableTickets = signal<number | null>(null);
  public maxTiquetsPerUser = signal<number | null>(null);

  // Coupon
  public couponInput = signal('');
  public couponApplying = signal(false);
  public couponError = signal<string | null>(null);
  public appliedCoupon = signal<TicketCouponPreviewResponseDTO | null>(null);
  /** Code kept active across quantity changes until the user removes it. */
  private activeCouponCode = signal<string | null>(null);
  public readonly hasActiveCoupon = computed(() => !!this.activeCouponCode());
  private couponPreviewSub: Subscription | null = null;
  public displayTotal = computed(() => {
    const applied = this.appliedCoupon();
    if (applied) {
      return applied.totalWithFee;
    }
    return this.totalPrice();
  });

  // User step  
  private userData = {};
  private customFieldValuesFromForm: Record<number, unknown> = {};
  public submitAttempt = new EventEmitter();

  // Holder steps
  public holderFields = signal<CustomFieldDTO[]>([]);
  public holdersData = signal<HolderPurchaseData[]>([]);
  public holdersDataWithCustomFields = computed(() => {
      return this.holdersData().some(x=> x.customFields.length > 0);
  })
  public pricesNames = computed(() => {
    const names = this.holdersData().map(x => x.priceName);    
    return names;
  })

  // Community 
  private community = toSignal(this.communityService.Get(), { initialValue: null });
  public corpColor = computed(() => {
    return this.community() ? this.community().color1 : '#000000';
  })
  public communityCode = computed(() => {
    return this.community() ? this.community().code : '';
  })
  public readonly ticketsAsEntradasCommunity = TICKETS_AS_ENTRADAS_COMMUNITY;
  public readonly couponEnabledCommunity = 'iii-congres-catalunya-circular';
  public readonly showCouponInput = computed(() =>
    this.communityCode() === this.couponEnabledCommunity
  );

  public privacyUrl: string;
  public termsUrl: string;
    
  ngOnInit(): void {
    this.ref.onClose.subscribe(() => {
      this.totalPrice.set(0);
    })

    setTimeout(() => {
      this.initSwiper()
    }, 0);
    
    this.ticket.set(this.dialogConfig.data.ticket);
    this.waitingListUserDetails = this.dialogConfig.data.waitingListUserDetails ?? null;
    this.reloadMaxTickets();    
    this.customFieldsForm.set(this.ticket().customFields.filter(x=> x.publicPermissions == 'rw'));

    if (this.ticket().customFields.length > 0 && this.ticket().multipleInscriptions) {
      const fields = this.ticket().customFields.filter(x=> x.publicPermissions === 'rw');
      this.customFieldsForm.set(fields.filter(x=> x.fieldLocation === 'PARENT'));
      this.holderFields.set(fields.filter(x=> x.fieldLocation === 'CHILDREN'));      
    }

    let langQueryParam = ['es', 'ca', 'en'].includes(this.translate.currentLang) ? `?lang=${this.translate.currentLang}` : '';

    this.privacyUrl = `${API_URL}/${this.communityCode}/policies/privacy` + langQueryParam;
    this.termsUrl = `${API_URL}/${this.communityCode}/policies/terms` + langQueryParam;

  }

  ngOnDestroy(): void {
    this.couponPreviewSub?.unsubscribe();
    if (this.reservedToken()) {
      this.cancelPlaces(false);
    }
  }      

  
  //#region TICKET

  private reloadMaxTickets() {
    if (this.ticket()?.maxByUser) {
      this.maxTiquetsPerUser.set(this.ticket().maxByUser - (this.quantity()));
    }

    if (this.availableTickets() != null) {
      if (this.maxTiquetsPerUser() == null || this.availableTickets() < this.maxTiquetsPerUser()) {
        this.maxTiquetsPerUser.set(this.availableTickets());
      }
    }
  }

  private getTicketDetails() {
    return this.ticket().prices
      .filter(x => x.qty && x.qty != 0)
      .map(x => ({
        quantity: x.qty,
        ticketPriceId: x.id
      }));
  }

  //#endregion

  //#region SWIPER

  public onSlideLoaded(event: boolean) {    
    setTimeout(() => this.swiper.swiper.updateAutoHeight(), 0)
  }

  private initSwiper() {
    this.swiperLoaded.set(true);
    setTimeout(() => this.swiper = this.swiperRef.nativeElement, 50)
  }  

  public slideChange(event) {        
    this.currentSlide.set(event?.detail?.[0]?.activeIndex);    
  }

  public changeStep(event, type) {
    this.swiper.swiper.updateAutoHeight();
    
    switch(type) {
      case 'ticket':                       
        this.onTicketsStepChange(event);        
        break;
      case 'user':
        this.onUserDataStepChange({
          userData: event['user-data'],
          formValid: event.formValid,
          customFieldValues: event.customFieldValues ?? {}
        });
        break;
      case 'holder':
        this.onHolderStepChange(event);
        break;
      case 'payment':
        this.canContinue.set(true);
        break;
    }
  }

  public async next() {

    if (this.currentSlide() > 0){

      this.submitAttempt.emit();

      if (!this.formIsValid()){
        Swal.fire({
          position: "top" ,
          title: this.translate.instant('GENERIC.bad'),
          icon: 'warning',
          text: this.translate.instant('CheckFormFields'),
          confirmButtonText: this.translate.instant('Continue')
        });
        return;
      }
      
      if (this.currentSlide() === this.lastSlide()) {
        this.purchase();
        return;
      } 
    } else if (this.currentSlide() === 0) {
      this.createForms();
      const totalSlides = 2 + (this.holdersDataWithCustomFields() ? this.holdersData().length : 0);
      this.lastSlide.set(totalSlides - 1);      
      this.buttonLoading.set(true);
      const details = this.getTicketDetails();
      try {
        const accessCode = this.waitingListUserDetails?.accessCode ?? null;
        const { token, seconds } = await firstValueFrom(this.ticketsService.reservePlaces(this.communityCode(), this.ticket().id, details, accessCode));
        this.reservedToken.set(token);
        this.reservedSeconds.set(seconds);
        this.reservedUntil.set(DateTime.now().plus({ seconds: seconds }));
      } catch (error) {
        Swal.fire({
          title: this.translate.instant('Oops'),
          text: this.translate.instant('ReservePlaces.NoMorePlacesAvailable'),
          icon: 'warning',
        });

        this.ref.close();
        return; 
      } finally { 
        this.buttonLoading.set(false);
      }
    }
  
    this.swiper.swiper.slideNext();
    this.canContinue.set(false);
  }

  public async prev() {
    if (this.currentSlide() === 1 && this.reservedToken()) {
      this.buttonPrevLoading.set(true);
      await this.cancelPlaces();
      this.buttonPrevLoading.set(false);
    }

    this.swiper.swiper.slidePrev();
    this.canContinue.set(true);
  }    

  //#endregion

  //#region TICKETS CHOOSE

  private onTicketsStepChange({ total, totalQty }) {
    this.totalPrice.set(total);
    this.quantity.set(totalQty);
    this.canContinue.set(!this.setTicketsCorrect(totalQty));

    const activeCode = this.activeCouponCode();
    if (!activeCode) {
      return;
    }

    if (totalQty === 0) {
      // Keep the code active, but clear preview until there is a cart again.
      this.appliedCoupon.set(null);
      this.couponError.set(null);
      return;
    }

    this.previewCoupon(activeCode);
  }

  private setTicketsCorrect(totalQty: number): boolean {
    if (totalQty === 0) return true;
    if (this.waitingListUserDetails) {
      const maxByAccess = this.waitingListUserDetails.people - (this.waitingListUserDetails.alreadyBuyed ?? 0);
      return (this.availableTickets() !== null && totalQty > this.availableTickets()) ||
        totalQty > maxByAccess;
    }
    return (this.ticket().maxByUser !== null && totalQty > this.ticket().maxByUser) ||
    (this.ticket().maxSalesPurchase !== null && totalQty > this.ticket().maxSalesPurchase) ||
    (this.availableTickets() !== null && totalQty > this.availableTickets());
  }

  private createForms() {
    this.holdersData.set([]);

    const prices = this.ticket().prices;

    if (this.holdersData().length !== this.quantity()) {
      prices.forEach(price => {
        this.createForm(price);
      });
    }
  }

  private createForm(price) {            
    for (let i = 0; i < price.qty; i++) {
      const json = JSON.stringify(this.holderFields());
      const customFieldsCloned = JSON.parse(json) as CustomFieldDTO[];
      const holderData = {
        priceId: price.id,
        priceName: price.name,
        quantity: 1,
        customFields: customFieldsCloned
      };

      this.holdersData.update(current => [...current, holderData]);
    }
  }

  //#endregion

  //#region USER / RESPONSIBLE DATA

  private onUserDataStepChange({ userData, formValid, customFieldValues = {} }) {
    this.userData = userData;
    this.customFieldValuesFromForm = customFieldValues;
    this.formIsValid.set(formValid);
    this.updateResponsibleFields();
    this.canContinue.set(this.formIsValid());
  }

  private updateResponsibleFields() {
    this.customFieldsForm.update(current =>
      current.map(field => {
        const hasValueFromForm = Object.prototype.hasOwnProperty.call(this.customFieldValuesFromForm, field.id);
        const lookupKey: string = field.key?.toLowerCase();
        const valueFromUserData = lookupKey && (this.userData as any)[lookupKey];
        const newValue = hasValueFromForm
          ? this.customFieldValuesFromForm[field.id]
          : (lookupKey && valueFromUserData !== undefined
            ? valueFromUserData
            : (field as any).value);
        return { ...field, value: newValue } as CustomFieldDTO;
      })
    );
  }

  //#endregion

  // #region HOLDER DATA

  private onHolderStepChange({ customFields, formValid }, holderIndex = this.currentSlide() - 2) {      
    this.holdersData.update(current => {
      if (holderIndex == null || holderIndex < 0 || holderIndex >= current.length) return current;
      // immutable update: return a new array with only the target index replaced
      return current.map((item, idx) => idx === holderIndex ? { ...item, customFields } : item);
    });
    
    this.canContinue.set(formValid);
  }

  // #endregion

  //#region PURCHASE

  private purchase() {
    if (this.waitingListUserDetails) {
      const pendingFromList = this.waitingListUserDetails.people - (this.waitingListUserDetails.alreadyBuyed ?? 0);
      const maxTheyCouldBuy = this.availableTickets() != null ? Math.min(pendingFromList, this.availableTickets()!) : pendingFromList;
      const totalQty = this.quantity();
      const shouldWarn = totalQty < maxTheyCouldBuy;
      if (shouldWarn) { 
        Swal.fire({
          position: 'top',
          icon: 'warning',
          title: this.translate.instant('WAITING_LIST.LESS_THAN_AVAILABLE_TICKETS_TITLE'),
          html: this.translate.instant('WAITING_LIST.LESS_THAN_AVAILABLE_TICKETS_MESSAGE', { max: maxTheyCouldBuy, buying: totalQty }),
          showCancelButton: true,
          confirmButtonText: this.translate.instant('WAITING_LIST.CONFIRM_CONTINUE'),
          cancelButtonText: this.translate.instant('WAITING_LIST.CANCEL_EDIT_TICKETS'),
          reverseButtons: true,
          customClass: { confirmButton: 'swal-confirm-corp' },
        }).then(result => {
          if (result.isConfirmed) {
            this.doPurchase();
          } else if (result.dismiss === Swal.DismissReason.cancel) {
            this.ref.close();
          }
        });
        return;
      }
    }
    this.doPurchase();
  }

  public applyCoupon(): void {
    const code = this.couponInput()?.trim();
    if (!code) {
      this.couponError.set(this.translate.instant('TICKETS.COUPON.CODE_REQUIRED'));
      return;
    }
    if (this.quantity() === 0) {
      return;
    }

    this.previewCoupon(code, { persistOnSuccess: true });
  }

  public removeCoupon(): void {
    this.couponPreviewSub?.unsubscribe();
    this.couponPreviewSub = null;
    this.activeCouponCode.set(null);
    this.appliedCoupon.set(null);
    this.couponError.set(null);
    this.couponInput.set('');
    this.couponApplying.set(false);
    setTimeout(() => this.swiper?.swiper?.updateAutoHeight(), 0);
  }

  private previewCoupon(code: string, options?: { persistOnSuccess?: boolean }): void {
    this.couponPreviewSub?.unsubscribe();
    this.couponApplying.set(true);
    this.couponError.set(null);

    this.couponPreviewSub = this.ticketsService.previewCoupon(
      this.communityCode(),
      this.ticket().id,
      code,
      this.getTicketDetails()
    ).subscribe({
      next: (res) => {
        const responseCode = res.code?.trim();
        const inputCode = code.trim();
        if (responseCode && responseCode !== inputCode) {
          this.appliedCoupon.set(null);
          this.activeCouponCode.set(null);
          this.couponApplying.set(false);
          this.couponError.set(this.translate.instant('TICKETS.COUPON.NOT_FOUND'));
          setTimeout(() => this.swiper?.swiper?.updateAutoHeight(), 0);
          return;
        }

        this.appliedCoupon.set(res);
        this.couponInput.set(res.code || code);
        if (options?.persistOnSuccess !== false) {
          this.activeCouponCode.set(res.code || code);
        }
        this.couponApplying.set(false);
        setTimeout(() => this.swiper?.swiper?.updateAutoHeight(), 0);
      },
      error: (err) => {
        this.appliedCoupon.set(null);
        // Keep activeCouponCode so changing the cart again can re-validate the same coupon.
        this.couponApplying.set(false);
        this.couponError.set(this.mapCouponError(err));
        setTimeout(() => this.swiper?.swiper?.updateAutoHeight(), 0);
      }
    });
  }

  private mapCouponError(err: any): string {
    const code = err?.error?.code as string | undefined;
    const keyMap: Record<string, string> = {
      TicketCouponNotFound: 'TICKETS.COUPON.NOT_FOUND',
      TicketCouponInactive: 'TICKETS.COUPON.INACTIVE',
      TicketCouponExpired: 'TICKETS.COUPON.EXPIRED',
      TicketCouponNotStarted: 'TICKETS.COUPON.NOT_STARTED',
      TicketCouponUsageLimitReached: 'TICKETS.COUPON.USAGE_LIMIT',
      TicketCouponMinimumNotMet: 'TICKETS.COUPON.MINIMUM_NOT_MET',
      TicketCouponCodeRequired: 'TICKETS.COUPON.CODE_REQUIRED',
    };
    const key = code ? keyMap[code] : null;
    return this.translate.instant(key ?? 'TICKETS.COUPON.GENERIC_ERROR');
  }

  private doPurchase() {
    const accessCode = this.waitingListUserDetails?.accessCode ?? null;
    const couponCode = this.appliedCoupon()?.code ?? this.activeCouponCode();
    const request = {
      details: this.getTicketDetails(),
      responsibleCustomFields: this.customFieldsForm(),
      userData: this.userData,
      holdersData: this.holdersData(),
      termsAcceptedDate: DateTime.now().toISO(),
      ...(accessCode != null && { accessCode }),
      ...(couponCode ? { couponCode } : {})
    } as TicketPurchaseRequest;

    this.buttonLoading.set(true);

    this.ticketsService.purchaseMultiple(this.communityCode(), this.ticket().id, request, this.reservedToken(), accessCode)
    .subscribe({
      next:  async (res: PurchaseResultDTO) => {
        try {
          const paymentRes = await this.paymentsService.processPurchaseResult(res, null, this.getRemainingReservedSeconds());

          if (paymentRes == PaymentResult.SUCCESS){
            this.trackPurchase(res);
            if (this.waitingListUserDetails) {
              const dto = { ...this.waitingListUserDetails, inscribingNow: this.quantity() };
              this.apiService.updateTicketWaitingListAfterSuccess(this.communityCode(), dto).subscribe({
                next: (x) => {
                  this.buttonLoading.set(false);
                  if (x > 0) {
                    const pending = x;
                    const confirmKey = pending === 1 ? 'WAITING_LIST.CONFIRM_ONE_TICKETS' : 'WAITING_LIST.CONFIRM_MANY_TICKETS';
                    const descriptionKey = pending === 1 ? 'WAITING_LIST.DESCRIPTION_ONE_TICKETS' : 'WAITING_LIST.DESCRIPTION_MANY_TICKETS';
                    Swal.fire({
                      title: this.translate.instant('WAITING_LIST.TITLE_TICKETS', {
                        assigned: this.waitingListUserDetails.people - x,
                        requested: this.waitingListUserDetails.people
                      }),
                      icon: 'question',
                      position: 'top',
                      html: this.translate.instant(descriptionKey, { pending }),
                      showCancelButton: true,
                      confirmButtonText: this.translate.instant(confirmKey, { pending }),
                      cancelButtonText: this.translate.instant('WAITING_LIST.CANCEL_TICKETS'),
                      footer: `<small>${this.translate.instant('WAITING_LIST.FOOTER_TICKETS')}</small>`,
                      reverseButtons: true,
                      allowOutsideClick: false,
                      customClass: { confirmButton: 'swal-confirm-corp' },
                    }).then(result => {
                      if (result.isConfirmed) {
                        Swal.fire({
                          icon: 'success',
                          title: this.translate.instant('WAITING_LIST.CONTINUE_TITLE'),
                          text: this.translate.instant('WAITING_LIST.CONTINUE_MESSAGE_TICKETS'),
                          confirmButtonText: this.translate.instant('Accept'),
                          customClass: { confirmButton: 'swal-confirm-corp' },
                        }).then(() => {
                          this.ref.close({ clearAccessCode: true });
                          this.successMessage(this.translate.instant(ticketI18nKey(this.communityCode(), 'ReservedSuccess')), res.purchaseUuid);
                        });
                      } else if (result.dismiss === Swal.DismissReason.cancel) {
                        this.apiService.rejectFromTicketWaitingList(this.communityCode(), this.waitingListUserDetails.id).subscribe(() => {
                          Swal.fire({
                            icon: 'info',
                            title: this.translate.instant('WAITING_LIST.CANCELLED_TITLE'),
                            text: this.translate.instant('WAITING_LIST.CANCELLED_MESSAGE_TICKETS'),
                            confirmButtonText: this.translate.instant('WAITINGLIST.accordButton'),
                            customClass: { confirmButton: 'swal-confirm-corp' },
                          }).then(() => {
                            this.ref.close({ clearAccessCode: true });
                            this.successMessage(this.translate.instant(ticketI18nKey(this.communityCode(), 'ReservedSuccess')), res.purchaseUuid);
                          });
                        });
                      }
                    });
                  } else {
                    this.ref.close({ clearAccessCode: true });
                    this.successMessage(this.translate.instant(ticketI18nKey(this.communityCode(), 'ReservedSuccess')), res.purchaseUuid);
                  }
                },
                error: () => {
                  this.buttonLoading.set(false);
                  this.ref.close({ clearAccessCode: true });
                  this.successMessage(this.translate.instant(ticketI18nKey(this.communityCode(), 'ReservedSuccess')), res.purchaseUuid);
                }
              });
            } else {
              this.buttonLoading.set(false);
              this.ref.close();
              this.successMessage(this.translate.instant(ticketI18nKey(this.communityCode(), 'ReservedSuccess')), res.purchaseUuid);
            }
          } else if (paymentRes == PaymentResult.FAILED){
            this.buttonLoading.set(false);
            this.failedMessage(this.translate.instant('PAYMENT.PaymentFailure'));
            this.cancelPlaces(false);
          } else {
            this.buttonLoading.set(false);
            this.cancelPlaces(false);
            this.ref.close();
          }
        } catch (error) {
          this.buttonLoading.set(false);
          this.failedMessage();
        }
      },
      error: (res) => {
        this.buttonLoading.set(false);
        this.cancelPlaces(false);
      }
    });
  }

  private trackPurchase(res: PurchaseResultDTO): void {
    if (!res?.purchaseUuid) {
      return;
    }

    const ticket = this.ticket();
    const value = Number(res.totalAmount ?? this.displayTotal() ?? 0);
    const rawItems = (ticket?.prices ?? [])
      .filter(price => price.qty && price.qty !== 0)
      .map(price => ({
        item_id: String(price.id ?? ticket.id),
        item_name: price.name || ticket.title,
        item_category: 'ticket',
        price: Number(price.totalWithFee ?? price.total ?? 0),
        quantity: price.qty,
      }));

    const rawSum = rawItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
    const items = rawSum > 0 && value !== rawSum
      ? rawItems.map(item => ({
          ...item,
          price: (item.price * value) / rawSum,
        }))
      : rawItems;

    pushPurchaseEventToGtm({
      transaction_id: res.purchaseUuid,
      value,
      community_code: this.communityCode(),
      items,
    });
  }

  private successMessage(title, ticketPurchaseUuid){
    const code = this.communityCode();
    const htmlMsg = this.translate.instant(ticketI18nKey(code, 'MailConfirmationSent')) + `
      <br>
      <a target='_blank' href='/${code}/tickets/print/${ticketPurchaseUuid}?print=true' style='background-color: var(--color-primary) !important' class='btn download-btn'>
        <i class="fa-solid fa-file-arrow-down"></i> ${this.translate.instant(ticketI18nKey(code, 'Download'))}
      </a>`;

    Swal.fire({
      title: title,
      html: htmlMsg,
      icon: 'success',
      confirmButtonText: this.translate.instant('Accept'),
    })
  }

  private async failedMessage(message?: string){  
    console.warn('ERROR --> ', message);

    Swal.fire({
      title: this.translate.instant('Oops'),
      text: message || this.translate.instant('ERRORS.Generic'),
      icon: message ? 'warning' : 'error',
      confirmButtonText: this.translate.instant('Accept'),
    });
  }

  private async cancelPlaces(awaitRequest = true) {
    const token = this.reservedToken();
    if (!token) {
      return;
    }

    const executeCancelPlacesRequest = () =>
      firstValueFrom(this.ticketsService.cancelReservedPlaces(this.communityCode(), this.ticket().id, token));

    if (awaitRequest) {
      await executeCancelPlacesRequest();
    } else {
      executeCancelPlacesRequest();
    }

    this.reservedToken.set(null);
    this.reservedSeconds.set(null);
    this.reservedUntil.set(null);
  }

  private getRemainingReservedSeconds(): number | null {
    if (this.reservedUntil()) {
      const now = DateTime.now();
      const diff = this.reservedUntil().diff(now, 'seconds').seconds;
      return diff > 0 ? Math.floor(diff) : 0;
    }
  }

  public countdownEnd(e: CountdownEvent) {
    if (e.action !== 'done' || e.left !== 0) {
      return;
    }

    const until = this.reservedUntil();
    if (!until || !this.reservedToken() || DateTime.now() < until) {
      return;
    }

    Swal.fire({
      title: this.translate.instant('Oops'),
      html: this.translate.instant('ReservePlaces.TimeExpired'),
      icon: 'warning',
      confirmButtonText: this.translate.instant('Accept'),
    });
    this.cancelPlaces(false);
    this.ref.close();
  }  

  //#endregion
}
